import type { MetricDef } from '@nono/contract';
import type { ReactNode } from 'react';
import { MetricInfoTrigger } from '@/components/data/MetricInfo';
import type { MetricRow } from '@/components/data/MetricsTable';
import { Sparkline } from '@/components/data/Sparkline';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { ACCENT } from '@/lib/chart';
import { formatMetric } from '@/lib/format';
import { useT, useViewMessages } from '@/lib/i18n';
import { FAMILY_ACCENT, familyOf } from '@/lib/metric-family';
import { useMetrics } from '@/lib/queries';

const MESSAGES = {
  nb: { title: 'Trender', sub: 'Daglig bevegelse i perioden (siste verdi vist).' },
  en: { title: 'Trends', sub: 'Per-day movement across the range (latest value shown).' },
};

// 'all' shows a hand-picked headline subset (parity with the legacy renderTrends).
const ALL_KEYS = [
  'adSpend',
  'bookings',
  'showedUp',
  'soldMemberships',
  'cpa',
  'rRoi',
  'ctr',
  'cpl',
];

export function TrendsPage() {
  const t = useT();
  const m = useViewMessages(MESSAGES);
  const metricsQuery = useMetrics();

  if (metricsQuery.isLoading)
    return (
      <PageFrame m={m}>
        <LoadingState />
      </PageFrame>
    );
  if (metricsQuery.isError || !metricsQuery.data)
    return (
      <PageFrame m={m}>
        <ErrorState onRetry={() => metricsQuery.refetch()} />
      </PageFrame>
    );

  const data = metricsQuery.data;
  const rows = data.rows as MetricRow[];

  const keys =
    data.segment === 'all'
      ? ALL_KEYS
      : data.defs
          .filter((d) => d.decision && ['int', 'nok', 'pct', 'ratio'].includes(d.format))
          .map((d) => d.key);

  const cards = keys
    .map((key) => data.defs.find((d) => d.key === key))
    .filter((d): d is MetricDef => Boolean(d));

  if (rows.length === 0 || cards.length === 0)
    return (
      <PageFrame m={m}>
        <EmptyState title={t.noData()}>{t.noDataHint()}</EmptyState>
      </PageFrame>
    );

  return (
    <PageFrame m={m}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {cards.map((def) => {
          const series = rows.map((r) => {
            const v = r[def.key];
            return typeof v === 'number' ? v : null;
          });
          const latest = [...series].reverse().find((v) => v !== null) ?? null;
          const color = ACCENT[FAMILY_ACCENT[familyOf(def.key)]];
          return (
            <div key={def.key} className="rounded-lg border border-border bg-surface p-3 sm:p-4">
              <div className="text-label text-fg-muted">
                <MetricInfoTrigger metricKey={def.key} label={def.label} />
              </div>
              <div className="mt-1 text-title text-fg tabular">
                {formatMetric(latest, def.format)}
              </div>
              <div className="mt-3">
                <Sparkline values={series} color={color} />
              </div>
            </div>
          );
        })}
      </div>
    </PageFrame>
  );
}

function PageFrame({ m, children }: { m: (typeof MESSAGES)['nb']; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-title text-fg">{m.title}</h1>
        <p className="mt-1 text-body text-fg-muted">{m.sub}</p>
      </header>
      {children}
    </div>
  );
}
